import { useEffect, useRef, useState } from 'react'
import gameSocket from '../features/crash/services/crashSocket'
import useAppStore from '../store/useAppStore'
import useUpdateBlocker from './useUpdateBlocker'
import { isRoundSnapshotUpdateBlocked } from '../services/appUpdateSafety'

const normalizeStatus = (phase) => ({ betting: 'started', drawing: 'raffle', finished: 'result' })[phase] || phase || 'new'
const betKey = (bet) => String(bet.id ?? bet.bet_id ?? bet.client_id ?? `${bet.chat_id}:${bet.created_at ?? ''}:${bet.color ?? bet.zone}:${bet.sum}:${bet.crystal}:${JSON.stringify(bet.bet_items ?? [])}`)

const normalizeBet = (bet, chatId, ownClientIds) => ({
  ...bet,
  your_bet: !useAppStore.getState().isGuest && Boolean(bet.your_bet || bet.you
    || (chatId != null && String(bet.chat_id) === String(chatId))
    || (bet.client_id && ownClientIds.has(String(bet.client_id))))
})

const applyWinners = (bets, winners, side, winningSide) => {
  if (!Array.isArray(winners)) return bets
  const byChat = new Map(winners.map((winner) => [String(winner.chat_id), winner]))
  const paid = new Set()
  return bets.map((bet) => {
    const winner = byChat.get(String(bet.chat_id))
    const won = bet[side] === winningSide
    const first = won && !paid.has(String(bet.chat_id))
    if (winner && first) paid.add(String(bet.chat_id))
    return { ...bet, win: won, win_crystal: winner && first ? winner.win_crystal : null, win_skin: winner && first ? winner.win_skin : null }
  })
}

export default function useRealtimeGame({ game, store, side, onResult, onMyResult, onReconnect, onInventory }) {
  const onResultRef = useRef(onResult)
  const onMyResultRef = useRef(onMyResult)
  const onReconnectRef = useRef(onReconnect)
  const onInventoryRef = useRef(onInventory)
  const pendingRef = useRef(new Map())
  const counterRef = useRef(0)
  const [showReconnectNotice, setShowReconnectNotice] = useState(false)
  const isGuest = useAppStore((state) => state.isGuest)
  useUpdateBlocker(() => !isGuest && (pendingRef.current.size > 0 || isRoundSnapshotUpdateBlocked(store.getState())))
  onResultRef.current = onResult
  onMyResultRef.current = onMyResult
  onReconnectRef.current = onReconnect
  onInventoryRef.current = onInventory

  useEffect(() => {
    let disposed = false
    let inventoryRequest = null
    let reconnectTimer = null
    const requests = new Set()
    const pending = pendingRef.current
    const chatId = isGuest ? null : gameSocket.resolveChatId()
    const isCurrentAuth = () => !useAppStore.getState().isGuest && gameSocket.authToken && gameSocket.resolveAuthToken() === gameSocket.authToken
    const waitForSnapshot = () => {
      if (reconnectTimer || store.getState().engine !== 'node') return
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null
        setShowReconnectNotice(true)
      }, 3000)
    }
    const hideReconnectNotice = () => {
      if (reconnectTimer) clearTimeout(reconnectTimer)
      reconnectTimer = null
      setShowReconnectNotice(false)
    }
    const updateRound = (change) => {
      store.setState((state) => {
        if (state.engine !== 'node') return state
        const round = state.activeRound
        const next = typeof change === 'function' ? change(round, state) : change
        return next === undefined ? state : { activeRound: next }
      })
    }
    const roundMatches = (round, data) => round && String(round.id) === String(data.round_id)
    const currentTime = () => Date.now() + gameSocket.clockOffset
    const fillTimes = (round) => ({
      ...round,
      second_to_raffle: round.ends_at ? Math.max(0, (round.ends_at - currentTime()) / 1000) : 0,
      second_to_finish_raffle: round.anim_ends_at ? Math.max(0, (round.anim_ends_at - currentTime()) / 1000) : Number(round.raffle_time || 0)
    })
    const refreshRound = async () => {
      const controller = new AbortController()
      requests.add(controller)
      try {
        await store.getState().loadActiveRound(controller.signal)
      } catch {
        if (!disposed && !controller.signal.aborted && !store.getState().engine) store.setState({ engine: 'worker' })
      } finally {
        requests.delete(controller)
      }
    }
    const settle = (id, error) => {
      const entry = pendingRef.current.get(String(id))
      if (!entry) return
      clearTimeout(entry.timer)
      pendingRef.current.delete(String(id))
      store.setState({ isBetting: pendingRef.current.size > 0 })
      if (error) entry.reject(error)
      else entry.resolve(true)
    }
    const handlers = {
      [`${game}:state`]: (data) => {
        hideReconnectNotice()
        if (data.engine === 'node') requests.forEach((controller) => controller.abort())
        if (data.server_time) gameSocket.clockOffset = Number(data.server_time) - Date.now()
        const source = data.round
        const ownBetAcks = data.engine === 'node' && isCurrentAuth()
          ? store.getState().ownBetAcks.filter((bet) => String(bet.round_id) === String(source?.round_id))
          : []
        store.setState({ engine: data.engine, isModeDisabled: Boolean(data.disabled), limits: data.limits, isConnected: true, ownBetAcks, pendingItemLocks: data.engine === 'node' ? {} : store.getState().pendingItemLocks })
        if (data.engine !== 'node') return
        const ownClientIds = new Set([...ownBetAcks.map((bet) => String(bet.client_id)), ...pending.keys()])
        store.setState({ activeRound: source ? fillTimes({
          ...source,
          id: source.round_id,
          status: normalizeStatus(source.phase),
          bets: applyWinners((source.bets || []).map((bet) => normalizeBet(bet, chatId, ownClientIds)), source.winners, side, source[side === 'color' ? 'win_color' : 'win_zone']),
          ...(data.limits || {}),
          ...(source.sums || {})
        }) : null })
        if (source?.win_sector != null && normalizeStatus(source.phase) === 'result') {
          onResultRef.current?.({ round_id: source.round_id, win_sector: source.win_sector, win_color: source.win_color, win_zone: source.win_zone })
        }
      },
      [`${game}:round`]: (data) => {
        store.setState({ isModeDisabled: false, ownBetAcks: [] })
        updateRound((round) => fillTimes({
          id: data.round_id, status: 'new', bets: [], stats_100: round?.stats_100 || {},
          prev_round: data.prev_round, ends_at: data.ends_at, round_time: data.round_time,
          raffle_time: data.raffle_time, result_time: data.result_time,
          min_bet: data.min_bet, max_bet: data.max_bet
        }))
      },
      [`${game}:bets`]: (data) => {
        updateRound((round, state) => {
          if (!roundMatches(round, data)) return undefined
          const bets = [...(round.bets || [])]
          const seen = new Set(bets.map(betKey))
          const ownClientIds = new Set([...state.ownBetAcks.map((bet) => String(bet.client_id)), ...pending.keys()])
          for (const raw of data.bets || []) {
            const bet = normalizeBet(raw, chatId, ownClientIds)
            const key = betKey(bet)
            if (!seen.has(key)) { bets.push(bet); seen.add(key) }
          }
          return { ...round, bets, ...Object.fromEntries(Object.entries(data).filter(([key]) => key.startsWith('sum_'))) }
        })
        if (String(store.getState().activeRound?.id) === String(data.round_id)) {
          for (const bet of data.bets || []) {
            if (isCurrentAuth() && bet.client_id && pending.has(String(bet.client_id))) settle(bet.client_id)
          }
        }
      },
      [`${game}:bet:ok`]: (data) => {
        if (!isCurrentAuth()) return
        if (data.client_id && String(store.getState().activeRound?.id) === String(data.round_id)) {
          store.setState((state) => ({
            ownBetAcks: state.ownBetAcks.some((bet) => bet.client_id === data.client_id)
              ? state.ownBetAcks
              : [...state.ownBetAcks, data],
            activeRound: {
              ...state.activeRound,
              bets: (state.activeRound.bets || []).map((bet) => bet.client_id === data.client_id ? { ...bet, your_bet: true } : bet)
            }
          }))
        }
        settle(data.client_id)
      },
      [`${game}:raffle`]: (data) => updateRound((round) => roundMatches(round, data)
        ? fillTimes({ ...round, status: 'raffle', win_sector: data.win_sector, win_color: data.win_color, win_zone: data.win_zone, anim_ends_at: data.anim_ends_at })
        : undefined),
      [`${game}:result`]: (data) => {
        updateRound((round) => roundMatches(round, data) ? {
          ...round, status: 'result', win_sector: data.win_sector, win_color: data.win_color,
          win_zone: data.win_zone, winners: data.winners,
          bets: applyWinners(round.bets || [], data.winners, side, data[side === 'color' ? 'win_color' : 'win_zone'])
        } : undefined)
        onResultRef.current?.(data)
      },
      [`${game}:winners`]: (data) => updateRound((round) => roundMatches(round, data) ? {
        ...round, winners: data.winners,
        bets: applyWinners(round.bets || [], data.winners, side, round[side === 'color' ? 'win_color' : 'win_zone'])
      } : undefined),
      [`${game}:my_result`]: (data) => { if (isCurrentAuth()) onMyResultRef.current?.(data) },
      [`${game}:engine`]: ({ mode }) => {
        store.setState({ engine: mode, isConnected: mode !== 'node' })
        if (mode === 'node') gameSocket.requestState(game)
        else refreshRound()
      },
      [`${game}:disabled`]: () => store.setState({ isModeDisabled: true, activeRound: null }),
      'user:balance': ({ balance }) => {
        if (gameSocket.authToken && gameSocket.resolveAuthToken() === gameSocket.authToken && balance != null && Number.isFinite(Number(balance))) store.setState({ balance: Number(balance) })
      },
      'user:inventory': () => {
        if (!gameSocket.authToken || gameSocket.resolveAuthToken() !== gameSocket.authToken || store.getState().engine !== 'node' || inventoryRequest) return
        inventoryRequest = store.getState().loadBetForm()
          .then(() => { if (!disposed) onInventoryRef.current?.() })
          .catch(() => {})
          .finally(() => { inventoryRequest = null })
      },
      'user:alert': (data) => {
        if (data.game && data.game !== game) return
        if (data.code === 'engine_switched') {
          store.setState({ engine: 'worker' })
          refreshRound()
        }
        if (data.client_id) settle(data.client_id, new Error(data.code))
        else if (pendingRef.current.size === 1) settle(pendingRef.current.keys().next().value, new Error(data.code))
      }
    }
    let wasConnected = false
    const onConnect = () => {
      store.setState({ isConnected: store.getState().engine !== 'node' })
      if (store.getState().engine === 'node') waitForSnapshot()
      else hideReconnectNotice()
      if (wasConnected) onReconnectRef.current?.()
      wasConnected = true
    }
    const onDisconnect = () => {
      store.setState({ isConnected: false })
      waitForSnapshot()
      pending.forEach((_, id) => settle(id, new Error('disconnected')))
    }
    Object.entries(handlers).forEach(([name, handler]) => gameSocket.on(name, handler))
    gameSocket.onConnect(onConnect)
    gameSocket.onDisconnect(onDisconnect)
    gameSocket.subscribe(game)
    if (gameSocket.isConnected()) onConnect()
    else waitForSnapshot()
    if (store.getState().engine !== 'node' || !store.getState().isConnected) refreshRound()
    return () => {
      disposed = true
      if (reconnectTimer) clearTimeout(reconnectTimer)
      requests.forEach((controller) => controller.abort())
      Object.entries(handlers).forEach(([name, handler]) => gameSocket.off(name, handler))
      gameSocket.offConnect(onConnect)
      gameSocket.offDisconnect(onDisconnect)
      gameSocket.unsubscribe(game)
      pending.forEach(({ timer, reject }) => { clearTimeout(timer); reject(new Error('disconnected')) })
      pending.clear()
      store.setState({ isConnected: false, isBetting: false })
    }
  }, [game, side, store, isGuest])

  const placeBet = ({ roundId, crystal, items, ...selection }) => new Promise((resolve, reject) => {
    if (useAppStore.getState().isGuest) {
      reject(new Error('auth_required'))
      return
    }
    const state = store.getState()
    if (state.engine !== 'node' || !state.isConnected || !gameSocket.isConnected()) {
      reject(new Error('disconnected'))
      return
    }
    if (state.activeRound?.id !== roundId || state.activeRound.ends_at <= Date.now() + gameSocket.clockOffset) {
      reject(new Error('round_closed'))
      return
    }
    const clientId = `${Date.now().toString(36)}-${(++counterRef.current).toString(36)}-${Math.random().toString(36).slice(2, 9)}`
    const timer = setTimeout(() => {
      const entry = pendingRef.current.get(clientId)
      if (!entry) return
      pendingRef.current.delete(clientId)
      store.setState({ isBetting: pendingRef.current.size > 0 })
      if (gameSocket.isConnected()) gameSocket.requestState(game)
      entry.reject(new Error('bet_unconfirmed'))
    }, 3000)
    pendingRef.current.set(clientId, { resolve, reject, timer })
    store.setState({ isBetting: true })
    gameSocket.emit(`${game}:bet`, {
      round_id: roundId, [side]: selection[side], crystal: Math.floor(Number(crystal) || 0),
      items: Array.isArray(items) ? items : [], client_id: clientId
    })
  })

  return { placeBet, requestState: () => gameSocket.requestState(game), clockOffset: gameSocket.clockOffset, showReconnectNotice }
}
